import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import EmptyPage from '../../../empty-page/emptyPage';
import CommonTable from '../../../reusable-ui-components/commonTable/CommonTable';
import Pagination from '../../../pagination/Pagination';
import { orgStayBillingFun } from '../../../../api/orgCampAPI';
import errorHandle from '../../../../utils/errorHandle';
import { day } from '../../orgStay/stayColumns';
import styles from '../../orgStay/orgStay.module.css';

/*
 * Organizer > Camp > Billing: one row per website / app booking — what the
 * guest paid for you, our commission and what you get, and where the payout is.
 * Payouts are worked out after check-out (bookings not checked out yet show
 * an estimate). Manual bookings aren't listed (you collected that money).
 */
const PAYOUT = {
    'not-eligible': { label: 'After check-out', tone: 'neutral' },
    generated: { label: 'Payout due', tone: 'warn' },
    completed: { label: 'Paid', tone: 'good' }
};
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const columns = [
    {
        label: 'Stay',
        accessor: 'contentId',
        render: (val, row) => (
            <div>
                <strong>{val?.title || 'N/A'}</strong>
                <div className={styles.muted}>{row.rooms}</div>
            </div>
        )
    },
    { label: 'Guest', accessor: 'guestName', render: (val, row) => `${val || 'N/A'} · ${row.guests || 0}` },
    {
        label: 'Stay dates',
        accessor: 'startDate',
        render: (val, row) => `${day(val)} – ${day(row.endDate)} (${row.nights || 0}N)`
    },
    {
        label: 'Booking',
        accessor: 'stayStatus',
        render: (val, row) => (
            <div>
                <span className={val === 'Cancelled' ? styles.bad : ''}>{val}</span>
                <div className={styles.muted}>{row.bookedVia}</div>
            </div>
        )
    },
    { label: 'Amount', accessor: 'payoutDetails', render: (val) => inr(val?.totalAmount) },
    { label: 'Commission', accessor: 'payoutDetails', render: (val) => inr(val?.commission) },
    {
        label: 'You get',
        accessor: 'payoutDetails',
        render: (val) => (
            <div>
                <strong>{inr(val?.amount)}</strong>
                {val?.estimated && <div className={styles.muted}>estimate</div>}
            </div>
        )
    },
    {
        label: 'Payout',
        accessor: 'payoutStatus',
        render: (val, row) => {
            const p = PAYOUT[val] || PAYOUT['not-eligible'];
            return (
                <span className={`${styles.pill} ${styles[p.tone]}`}>
                    {row.approval === 'not-approved' ? 'On hold' : p.label}
                </span>
            );
        }
    }
];

const CampBillings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get('status') || '';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);
            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) delete current[key];
                else current[key] = params[key];
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        let gone = false;
        setLoading(true);
        orgStayBillingFun({
            status: ['not-eligible', 'generated', 'completed'].includes(status) ? status : undefined,
            limit,
            offset: offSet
        })
            .then((res) => {
                if (gone) return;
                setRows(res?.data?.data || []);
                setTotalCount(res?.data?.totalCount || 0);
            })
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [status, offSet]);

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='customOptions'>
                <div className='sticky-container'>
                    <select
                        className='select'
                        aria-label='Payout status'
                        value={['not-eligible', 'generated', 'completed'].includes(status) ? status : ''}
                        onChange={(e) => updateURL({ status: e.target.value, page: 1 })}
                    >
                        <option value=''>All bookings</option>
                        <option value='not-eligible'>Not checked out yet</option>
                        <option value='generated'>Payout due</option>
                        <option value='completed'>Paid</option>
                    </select>
                </div>
            </div>
            {rows.length === 0 && !loading ? (
                <EmptyPage />
            ) : (
                <>
                    <CommonTable data={rows} columns={columns} setRowClickId={() => {}} />
                    <Pagination
                        paginationClick={(n) => Math.ceil(totalCount / limit) > 1 && updateURL({ page: n })}
                        numberOfRows={totalCount}
                        offSet={offSet}
                        pageNumber={pageNumber}
                        limit={limit}
                    />
                </>
            )}
        </div>
    );
};

export default CampBillings;
